/**
 * Site Branding Card — Super Admin editable copy for the SEO shell.
 * ------------------------------------------------------------------
 * The Organization / WebSite / SoftwareApplication JSON-LD block that
 * crawlers (Google, WhatsApp, LinkedIn, view-source) see BEFORE React
 * hydrates lives in `public/index.html`. It contains a) our 6 social
 * handles, b) the Organization description, c) the SoftwareApplication
 * description, and d) the feature list.
 *
 * Editing here writes to Mongo (`site_branding.singleton = "current"`).
 * A postbuild step (`scripts/inject-brand.js`) reads this on every
 * deploy and substitutes the token placeholders in build/index.html —
 * so the crawler-visible copy stays in sync with what's shown here.
 */
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Twitter, Linkedin, Instagram, Youtube, Facebook, Save,
  Loader2, Plus, Trash2, MessageSquare, RefreshCw, MessageCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

// Ordered set — determines the render order + the sameAs JSON-LD order.
const SOCIAL_PLATFORMS: {
  key: "twitter" | "linkedin" | "instagram" | "youtube" | "tiktok" | "facebook";
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  urlHint: string;
  color: string;
}[] = [
  { key: "twitter",   label: "Twitter / X", icon: Twitter,       urlHint: "https://twitter.com/socibaba",         color: "text-sky-500" },
  { key: "linkedin",  label: "LinkedIn",    icon: Linkedin,      urlHint: "https://www.linkedin.com/company/socibaba", color: "text-sky-700" },
  { key: "instagram", label: "Instagram",   icon: Instagram,     urlHint: "https://www.instagram.com/socibaba",   color: "text-rose-500" },
  { key: "youtube",   label: "YouTube",     icon: Youtube,       urlHint: "https://www.youtube.com/@socibaba",    color: "text-red-500" },
  // lucide has no first-class TikTok icon — MessageSquare stands in for the marketing card only.
  { key: "tiktok",    label: "TikTok",      icon: MessageSquare, urlHint: "https://www.tiktok.com/@socibaba",     color: "text-neutral-800 dark:text-neutral-200" },
  { key: "facebook",  label: "Facebook",    icon: Facebook,      urlHint: "https://www.facebook.com/socibaba",    color: "text-blue-600" },
];

type SocialHandle = { handle: string; url: string };
type BrandingResponse = {
  social_handles: Record<string, SocialHandle>;
  brand_tagline: string;
  brand_description: string;
  feature_list: string[];
  contact_fab_enabled: boolean;
  contest_url?: string;
  updated_at: string;
  same_as: string[];
};

export const SiteBrandingCard: React.FC = () => {
  const qc = useQueryClient();
  const q = useQuery<BrandingResponse>({
    queryKey: ["admin-site-branding"],
    queryFn: async () => (await api.get("/admin/site-branding")).data,
  });

  const [handles, setHandles] = useState<Record<string, SocialHandle>>({});
  const [tagline, setTagline] = useState("");
  const [description, setDescription] = useState("");
  const [features, setFeatures] = useState<string[]>([]);
  const [contactFabEnabled, setContactFabEnabled] = useState(false);
  const [contestUrl, setContestUrl] = useState("");

  useEffect(() => {
    if (!q.data) return;
    setHandles(q.data.social_handles || {});
    setTagline(q.data.brand_tagline || "");
    setDescription(q.data.brand_description || "");
    setFeatures(q.data.feature_list || []);
    setContactFabEnabled(!!q.data.contact_fab_enabled);
    setContestUrl(q.data.contest_url || "");
  }, [q.data]);

  const save = useMutation({
    mutationFn: async () =>
      (await api.patch("/admin/site-branding", {
        social_handles: handles,
        brand_tagline: tagline,
        brand_description: description,
        feature_list: features.filter((f) => f.trim()),
        contact_fab_enabled: contactFabEnabled,
        contest_url: contestUrl.trim(),
      })).data,
    onSuccess: () => {
      toast.success("Branding saved · redeploy to update view-source HTML for crawlers");
      qc.invalidateQueries({ queryKey: ["admin-site-branding"] });
      // Also nudge the public-site-config cache so the ContactFAB updates
      // in the current session without a hard reload.
      qc.invalidateQueries({ queryKey: ["public-site-config-contact-fab"] });
      qc.invalidateQueries({ queryKey: ["site-config-public"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const setHandle = (k: string, field: "handle" | "url", v: string) =>
    setHandles((prev) => ({ ...prev, [k]: { ...(prev[k] || { handle: "", url: "" }), [field]: v } }));

  return (
    <div className="sg-card p-6" data-testid="site-branding-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-orange-50 dark:bg-orange-950/40 flex items-center justify-center shrink-0">
          <RefreshCw className="h-5 w-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold text-[color:var(--sg-fg)]">Site Branding & Social</div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
            Truthful copy that ships to <code>public/index.html</code> — what Google, WhatsApp, LinkedIn,
            and <code>view-source:</code> see before JS runs. Redeploy to push edits to the crawler HTML.
          </div>
        </div>
      </div>

      {q.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-[color:var(--sg-muted)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading branding…
        </div>
      ) : (
        <div className="space-y-6">
          {/* Public widgets — the "Contact us" floating pill on marketing pages. */}
          <div
            className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 px-4 py-3"
            data-testid="contact-fab-toggle-row"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-fuchsia-600" />
                <Label htmlFor="contact-fab-toggle" className="text-sm font-semibold cursor-pointer">
                  Show "Contact us" floating pill
                </Label>
              </div>
              <p className="text-xs text-[color:var(--sg-muted)] mt-1">
                Bottom-left pill on marketing / public pages (auto-hides inside <code>/workspace</code>
                and <code>/admin</code>). When off, no visitor sees it.
              </p>
            </div>
            <Switch
              id="contact-fab-toggle"
              data-testid="contact-fab-toggle"
              checked={contactFabEnabled}
              onCheckedChange={setContactFabEnabled}
            />
          </div>

          {/* Contest / voting URL — surfaced by <VoterHero> on /?ref=vote. */}
          <div
            className="rounded-lg border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 px-4 py-3 space-y-2"
            data-testid="contest-url-row"
          >
            <div>
              <Label htmlFor="contest-url-input" className="text-sm font-semibold">
                Contest / Upvote URL
              </Label>
              <p className="text-xs text-[color:var(--sg-muted)] mt-1">
                The link the <strong>"Upvote SociBaba — Free"</strong> button on
                <code> /?ref=vote</code> opens. Paste the exact showcase URL from Emergent
                (e.g. <code>https://app.emergent.sh/showcase/&lt;handle&gt;/&lt;id&gt;</code>).
              </p>
            </div>
            <Input
              id="contest-url-input"
              data-testid="contest-url-input"
              value={contestUrl}
              onChange={(e) => setContestUrl(e.target.value)}
              placeholder="https://app.emergent.sh/showcase/..."
              className="h-9 text-xs font-mono"
            />
          </div>
          {/* Social handles */}
          <div>
            <Label className="text-sm font-semibold">Social profiles (6)</Label>
            <p className="text-xs text-[color:var(--sg-muted)] mt-1 mb-3">
              Populates the <code>sameAs</code> array in the Organization JSON-LD. Empty URLs are skipped.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SOCIAL_PLATFORMS.map(({ key, label, icon: Icon, urlHint, color }) => (
                <div key={key} className="p-3 rounded-lg border border-[color:var(--sg-border)]" data-testid={`social-${key}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`h-4 w-4 ${color}`} />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      value={handles[key]?.handle || ""}
                      onChange={(e) => setHandle(key, "handle", e.target.value)}
                      placeholder="@socibaba"
                      className="col-span-1 h-9 text-xs"
                      data-testid={`social-${key}-handle`}
                    />
                    <Input
                      value={handles[key]?.url || ""}
                      onChange={(e) => setHandle(key, "url", e.target.value)}
                      placeholder={urlHint}
                      className="col-span-2 h-9 text-xs"
                      data-testid={`social-${key}-url`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tagline */}
          <div>
            <Label htmlFor="brand-tagline" className="text-sm font-semibold">Brand tagline</Label>
            <p className="text-xs text-[color:var(--sg-muted)] mt-1 mb-2">
              Short one-liner used as the default meta description fallback.
            </p>
            <Input
              id="brand-tagline"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              maxLength={280}
              data-testid="brand-tagline-input"
            />
            <div className="text-[10px] text-[color:var(--sg-muted)] mt-1 text-right">{tagline.length}/280</div>
          </div>

          {/* Organization description */}
          <div>
            <Label htmlFor="brand-description" className="text-sm font-semibold">Organization & App description</Label>
            <p className="text-xs text-[color:var(--sg-muted)] mt-1 mb-2">
              Fills both Organization.description and SoftwareApplication.description in the JSON-LD.
              Keep it truthful — no fabricated stats, no dated positioning.
            </p>
            <Textarea
              id="brand-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={1200}
              data-testid="brand-description-input"
            />
            <div className="text-[10px] text-[color:var(--sg-muted)] mt-1 text-right">{description.length}/1200</div>
          </div>

          {/* Feature list */}
          <div>
            <Label className="text-sm font-semibold">Feature list</Label>
            <p className="text-xs text-[color:var(--sg-muted)] mt-1 mb-3">
              Each bullet becomes an entry in <code>featureList</code>. Max 12 items, 180 chars each.
            </p>
            <div className="space-y-2">
              {features.map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={f}
                    onChange={(e) => setFeatures((prev) => prev.map((x, idx) => (idx === i ? e.target.value : x)))}
                    maxLength={180}
                    placeholder="e.g. SibaScore 0-1000 — the trust rating buyers actually believe in"
                    className="flex-1 h-9 text-xs"
                    data-testid={`feature-${i}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFeatures((prev) => prev.filter((_, idx) => idx !== i))}
                    data-testid={`feature-${i}-delete`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
              {features.length < 12 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFeatures((prev) => [...prev, ""])}
                  data-testid="feature-add"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add feature
                </Button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-[color:var(--sg-border)]">
            <div className="text-xs text-[color:var(--sg-muted)]">
              Last updated: {q.data?.updated_at ? new Date(q.data.updated_at).toLocaleString() : "—"}
            </div>
            <Button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              data-testid="site-branding-save"
            >
              {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save branding
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
